import { z } from 'zod';
export const ToolVisibilitySchema = z.enum(['summary', 'hidden', 'detailed']);
export const SkillToolKeySchema = z.enum([
    'db.list_tables',
    'db.describe_table',
    'db.run_query',
    /** SQL 을 saved_queries 에만 저장하고 실행하지 않음. artifact 에서 참조할 queryId 반환. */
    'db.save_query',
    'excel.list_my_files',
    'excel.describe',
    'excel.query',
    'excel.head',
    /** SQL 을 saved_queries 에만 저장하고 실행하지 않음. artifact 에서 참조할 queryId 반환. */
    'excel.save_query',
    /** 첨부 문서(pdf/docx/pptx/hwp/hwpx/txt/md) 변환 마크다운을 offset/limit 으로 읽기. */
    'doc.read',
    /** 첨부 문서 변환 마크다운에서 키워드 검색 — 매칭 스니펫과 위치 반환 (큰 문서 탐색용). */
    'doc.search',
    /** 첨부 이미지의 저장된 OCR 텍스트(업로드 시 비전 LLM 추출)를 offset/limit 으로 읽기. */
    'image.read_text',
    /** 첨부 이미지를 비전 기본 모델로 다시 읽어 시각적 질문(차트/색/영역/레이아웃)에 답. prompt 동반. */
    'image.analyze',
    'web.fetch_url',
    /** Google AI Grounding (Search) 기반 외부 검색. Gemini 모델이 검색·합성한 답변과 출처를 반환. */
    'web.search_grounded',
    /** Gemini Nano Banana 이미지 생성. 결과 이미지를 MinIO 에 저장하고 표시용 URL 을 반환 (base64 미반환). */
    'image.generate',
    /** 등록된 HTTP endpoint 메타 조회 (Skill 화이트리스트 범위). */
    'http.list_endpoints',
    /** 등록된 HTTP endpoint 에 REST 요청. 인증/쿠키 서버 주입. */
    'http.request',
    /** 위키 페이지 — 카테고리별 슬러그/제목/takeaway 한 줄 인덱스 (LLM 컨텍스트 절약). */
    'wiki.list_index',
    /** 위키 페이지 — 페이지 목록 (카테고리/검색어 필터). */
    'wiki.list_pages',
    /** 위키 페이지 — 슬러그로 한 페이지의 전체 내용 + 백링크. */
    'wiki.read_page',
    /** 위키 페이지 — 제목/takeaway/본문 full-text 검색. */
    'wiki.search',
    /** Artifact (Canvas) — 모든 세션에서 자동 활성. admin UI 에서는 노출하지 않는다. */
    'system.create_artifact',
    'system.update_artifact',
    /** Dashboard — JSON spec 만 받아서 서버측 렌더러가 폴리시드 HTML 생성. 작은 LLM 도 안정적으로 결과 보장. */
    'dashboard.create',
    /** Dashboard 부분 수정 — 기존 대시보드에 작은 edit op 만 적용(전체 재생성 X). 세션에 대시보드 있을 때만 노출. */
    'dashboard.edit',
    /** HITL — 사용자에게 선택/자유 입력을 받고 응답을 tool_result 로 반환. 모든 세션에서 자동 활성. */
    'system.ask_user',
    /** HITL — 여러 질문을 한 번에 묶어 사용자에게 폼처럼 보여주고 응답을 받음. 모든 세션 자동 활성. */
    'system.batch_ask_user',
    /**
     * HITL — 다단계 분석 plan 을 사용자에게 보여주고 진행/수정/취소 선택을 받음.
     * cross-domain 또는 인과 추론 등 4단계 이상 예상되는 작업에서 LLM 이 먼저 호출해
     * 사용자 승인 후 실제 도구 호출에 들어간다. 모든 세션 자동 활성.
     */
    'system.propose_plan',
    /**
     * 다른 skill 에게 메시지를 위임해 처리하게 한다. 라우터 skill 패턴 (도매/소매 분기 등).
     * config.allowedSkillIds whitelist 로 호출 가능 대상 제한. 같은 turn 내 동일 (skill, message)
     * 재호출은 결과 캐시로 단락. 깊이/per-turn budget 으로 무한 재귀 차단.
     */
    'skill.call',
    /**
     * 본인 사용자 속성 (사번, 팀, 직급 등 user_attribute_definitions 에 정의된 값) 을 read-only 로 조회.
     * 사용자가 "내", "나의", "우리 팀" 같은 본인 지칭 표현을 쓸 때만 호출하도록 system prompt 에 가이드.
     * 매 turn 자동 주입은 없음 — on-demand. config.allowedKeys 로 노출 키 화이트리스트.
     * 같은 turn 내 동일 keys 호출은 결과 캐시로 단락.
     */
    'user.get_attributes',
    /** 슬라이드 — 현재 세션이 슬라이드와 1:1 바인딩일 때 사용. */
    'slide.read_deck',
    'slide.read_page',
    'slide.create_page',
    'slide.update_page',
    'slide.set_page_tsx',
    'slide.delete_page',
    'slide.reorder_pages',
    'slide.set_design',
    'slide.set_meta',
    'slide.list_assets',
    /** 현황분석 - Insight Agent 연동시 사용. */
    'analysis.verify_tag',
    'analysis.verify_detail',
    'analysis.apply_task',
    'analysis.get_data',
    /** 데이터탐색 - Insight Agent 연동시 사용. analysis.verify_tag (searchYn='Y') + verify_detail 다음에 분기. */
    'datasearch.split',
    'datasearch.analyze',
    'datasearch.expand',
    'datasearch.total_insight',
    'datasearch.grounding',
    /** 이메일 발송 — SMTP 경유. 수신자/제목/본문을 받아 메일을 보내고 결과 반환. */
    'email.send',
    /**
     * 등록된 응답 프롬프트 (response_prompts.slug) 를 sub-LLM 으로 실행해 결과를 받는다.
     * config.allowedPromptSlugs 화이트리스트 필수. system 메시지에 사용 가능 slug 목록 자동 주입.
     * endpoint 결정: prompt.preferred_endpoint_id → 챗 세션 endpoint → default.
     */
    'prompt.run',
]);
export const SkillToolSchema = z.object({
    toolKey: SkillToolKeySchema,
    config: z.record(z.unknown()),
});
export const SkillSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    systemPrompt: z.string(),
    toolVisibility: ToolVisibilitySchema,
    enabled: z.boolean(),
    tools: z.array(SkillToolSchema),
    /** cross-domain 시 함께 ranked 되길 권하는 동료 skill slug. classifier prompt 어노테이션. */
    pairsWellWith: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const SkillListSchema = z.object({ skills: z.array(SkillSchema) });
/** 사용자 챗 UI 가 쓰는 축약 — systemPrompt/createdBy 등 관리자 전용 필드 제외. */
export const PublicSkillSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    tools: z.array(z.object({ toolKey: SkillToolKeySchema })),
});
export const PublicSkillListSchema = z.object({
    skills: z.array(PublicSkillSchema),
});
export const CreateSkillRequestSchema = z.object({
    slug: z
        .string()
        .regex(/^[a-z0-9-]+$/, 'slug 은 소문자 영문/숫자/하이픈만 허용합니다.')
        .min(2)
        .max(40),
    name: z.string().min(1).max(80),
    description: z.string().max(2000).nullable().default(null),
    systemPrompt: z.string().max(10000).default(''),
    toolVisibility: ToolVisibilitySchema.default('summary'),
    enabled: z.boolean().default(true),
    tools: z.array(SkillToolSchema).default([]),
    /** classifier hint — 함께 ranked 되길 권하는 동료 skill slug 배열. 최대 8개. */
    pairsWellWith: z.array(z.string().min(1).max(40)).max(8).default([]),
});
export const UpdateSkillRequestSchema = CreateSkillRequestSchema.partial()
    .omit({ slug: true })
    .extend({
    /** admin 이 입력한 변경 메모 (선택). skill_prompt_versions.changelog 에 저장. */
    changelog: z.string().max(500).optional(),
});
export const SetSessionSkillRequestSchema = z.object({
    skillId: z.string().uuid().nullable(),
});
/** 활성 skill 칩 — 채팅 상단 표시 + 사용자 add/remove 용. */
export const ActiveSkillSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
});
export const ActiveSessionSkillsResponseSchema = z.object({
    /** 현재 세션에 활성화된 skill 목록 (chat_sessions.skillIds). */
    skills: z.array(ActiveSkillSchema),
    /** 사용자가 chip "+" picker 로 추가할 수 있는 권한 내 enabled skill (현재 active 제외, 최대 30). */
    available: z.array(ActiveSkillSchema).optional(),
});
/** 사용자가 채팅 chip 으로 skill 을 add/remove 할 때. 최대 4개 (MAX_PINNED). */
export const UpdateSessionSkillsRequestSchema = z.object({
    skillIds: z.array(z.string().uuid()).max(4),
});
//# sourceMappingURL=skills.js.map